import { TableRow as MuiTableRow, styled, TableCell, Tooltip } from '@mui/material'
import { addMinutes, minutesToMilliseconds } from 'date-fns'
import { ComponentProps, useMemo } from 'react'
import { RRule } from 'rrule'
import { AppointmentType, AvailabilityType } from '../../../../constants'
import { useLocale, useResource } from '../../../../hooks'
import { AvailabilityConstraint } from '../../../../types'
import { appointmentTypeToDisplayText } from '../../../../util'
import { NEVER_FREQUENCY, RULE_FREQUENCY_DELIMITER, UTC_TIME_ZONE } from '../../constants'
import { DtoCalendarConstraint } from '../../types'
import { getTheme } from '../../utils/getTheme'

type Props = Omit<ComponentProps<typeof MuiTableRow>, 'onClick'> & {
  constraint: AvailabilityConstraint
  onClick: (constraint: DtoCalendarConstraint) => void
}

const TableRow = styled(MuiTableRow)`
  cursor: pointer;
`

const DataCell = styled(TableCell)`
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.875rem;
`

const AvailabilityTypesCell = styled(TableCell)`
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.875rem;
  max-width: 10rem;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const makeAvailabilityConstraintToDtoCalendarConstraint =
  () =>
  (constraint: AvailabilityConstraint): DtoCalendarConstraint => {
    const {
      availability_type: availabilityType,
      appointment_types: appointmentTypes = [] as AppointmentType[],
      delivery_models,
      duration_in_minutes,
      id,
      recurrence_rule,
      is_all_day,
    } = constraint

    const { options } = RRule.fromString(recurrence_rule)

    const { dtstart } = options
    const timeZone = options.tzid as string
    const dateStart = new Date(dtstart.valueOf() + minutesToMilliseconds(new Date().getTimezoneOffset()))
    const dateEnd = addMinutes(dateStart, duration_in_minutes)
    const ruleFrequency = recurrence_rule ? recurrence_rule.split(RULE_FREQUENCY_DELIMITER)[1] : undefined
    const recurrenceFrequency = ruleFrequency === NEVER_FREQUENCY ? undefined : ruleFrequency

    const subject =
      availabilityType === AvailabilityType.AVAILABLE
        ? appointmentTypes?.map((type) => appointmentTypeToDisplayText[type]).join(', ')
        : 'Unavailable'

    return {
      availabilityType,
      appointmentTypes,
      deliveryModels: delivery_models,
      dateEnd,
      dateEndTimeZone: timeZone,
      dateStart,
      dateStartTimeZone: timeZone,
      id,
      isAllDay: is_all_day,
      recurrenceException: undefined, // TODO: Handle single occurrences
      recurrenceId: undefined, // TODO: Handle single occurrences
      recurrenceFrequency,
      subject,
    }
  }

const ruleToOccurrenceDisplayText = (rule: RRule) => {
  const { byweekday: byWeekday, freq: frequency } = rule.options
  const occurrence = new RRule({ byweekday: byWeekday, freq: frequency })

  // TODO: This needs to be i18n'd
  const temp = occurrence.toText().split('on ')[1]
  const tempDays = temp ? temp.split(', ') : []
  const shortDayNames = tempDays.map((day) => day.substring(0, 3))

  return shortDayNames.length > 0 ? shortDayNames.join(', ') : occurrence.toText()
}

const Constraint = ({ constraint, onClick, ...props }: Props) => {
  const { locale } = useLocale()
  const { resource } = useResource()
  const resourceTimeZone = resource?.timezone

  const availabilityConstraintToDtoCalendarConstraint = useMemo(
    () => makeAvailabilityConstraintToDtoCalendarConstraint(),
    [resourceTimeZone]
  )

  const onClickProxy = () => {
    const dtoConstraint = availabilityConstraintToDtoCalendarConstraint(constraint)

    onClick(dtoConstraint)
  }

  const rule = RRule.fromString(constraint.recurrence_rule)
  const { dtstart: dateStart, until } = rule.options
  const dateEnd = addMinutes(dateStart, constraint.duration_in_minutes)
  const occurrenceText = ruleToOccurrenceDisplayText(rule)

  if (constraint.is_all_day) {
    dateStart.setUTCHours(0, 0)
    dateEnd.setUTCHours(23, 59)
  }

  const startDateText = dateStart?.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: UTC_TIME_ZONE,
  })

  const startTimeText = dateStart?.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: UTC_TIME_ZONE })

  const endTimeText = dateEnd.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: UTC_TIME_ZONE,
  })

  const untilText = until?.toLocaleDateString(locale) ?? '-'
  const appointmentTypeText = constraint.appointment_types?.map((type) => appointmentTypeToDisplayText?.[type]).join(', ')
  const theme = getTheme(constraint.availability_type, constraint.appointment_types)
  const rowStyles = { borderLeft: `4px solid ${theme.accent}99` }

  return (
    <TableRow {...props} hover onClick={onClickProxy} key={constraint.id} style={rowStyles} tabIndex={0}>
      <AvailabilityTypesCell align="left" data-name="Appointment Types">
        <Tooltip placement="top" title={appointmentTypeText ?? 'Unavailable'}>
          <span>{appointmentTypeText ?? 'Blocked time'}</span>
        </Tooltip>
      </AvailabilityTypesCell>
      <DataCell align="center" data-name="Start Date">
        {startDateText}
      </DataCell>
      <DataCell align="center" data-name="From">
        {startTimeText}
      </DataCell>
      <DataCell align="center" data-name="To">
        {endTimeText}
      </DataCell>
      <DataCell align="center" data-name="End Date">
        {untilText}
      </DataCell>
      <DataCell align="left" data-name="Occurrence">
        {occurrenceText}
      </DataCell>
    </TableRow>
  )
}

export { Constraint }
